import { Component, Inject, OnInit, signal } from '@angular/core';
import { FolderData } from '@app/stores/foldersStore';
import { MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { DeliveryNotesService } from '../../../../api/services/delivery-notes.service';
import { ErrorHandler } from '@app/core/services/error-handler.service';
import { lastValueFrom, Observable } from 'rxjs';
import { BinaryFileType } from '../../../../api/models/binary-file-type';
import { DeliveryNoteBundleOverview } from '../../../../api/models/delivery-note-bundle-overview';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatDivider } from '@angular/material/list';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

@Component({
    selector: 'app-view-bundle-dialog',
    templateUrl: './view-bundle-dialog.component.html',
    styleUrls: ['./view-bundle-dialog.component.scss'],
    imports: [
        MatIconButton,
        MatTooltip,
        MatIcon,
        MatDialogClose,
        MatList,
        MatListItem,
        MatDivider,
        NgxExtendedPdfViewerModule,
        TranslateModule,
    ],
})
export class ViewBundleDialogComponent implements OnInit {
    deliveryNoteBundle:
        | FolderData
        | DeliveryNoteBundleOverview;
    downloadedPdfs: Blob[] = [];
    currentlyViewedPdf = signal<Blob | undefined>(undefined);

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data: {
            deliveryNoteBundle:
                | FolderData
                | DeliveryNoteBundleOverview;
            organizationSiteKey: string;
            context: 'consignor' | 'carrier' | 'consignee';
        },
        private translateService: TranslateService,
        private spinner: NgxSpinnerService,
        private deliveryNotesService: DeliveryNotesService,
        private errorHandler: ErrorHandler
    ) {}

    ngOnInit(): void {
        this.deliveryNoteBundle = this.data.deliveryNoteBundle;
        if (this.deliveryNoteBundle.numberOfDeliveryNotes > 0) {
            this.loadBundle();
        }
    }

    async loadBundle(): Promise<void> {
        try {
            const blobData = await lastValueFrom(
                this.getBlobObservableForContext(this.data.context)!
            );

            this.currentlyViewedPdf.set(blobData);
        } catch (error) {
            this.translateService.instant('GLOBAL.ERROR.UNSPECIFIED');
        }
    }

    async downloadDeliveryBundle(): Promise<void> {
        try {
            this.spinner.show();
            const url = window.URL.createObjectURL(this.currentlyViewedPdf()!);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const bundleNumber = this.data.deliveryNoteBundle.bundleNumber;
            a.download = bundleNumber.replace('.', '-') + '.pdf';
            a.click();
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    getBlobObservableForContext(
        context: string
    ): Observable<BinaryFileType> | undefined {
        switch (context) {
            case 'consignor':
                return this.deliveryNotesService.getDeliveryNotePdfsForBundle({
                    organizationSiteKey: this.data.organizationSiteKey,
                    deliveryNoteBundleKey: this.data.deliveryNoteBundle._key,
                });
            case 'carrier':
                return this.deliveryNotesService.getCarrierDeliveryNotePdfsForBundle(
                    {
                        organizationSiteKey: this.data.organizationSiteKey,
                        deliveryNoteBundleKey:
                            this.data.deliveryNoteBundle._key,
                    }
                );
            case 'consignee':
                return this.deliveryNotesService.getConsigneeDeliveryNotePdfsForBundle(
                    {
                        organizationSiteKey: this.data.organizationSiteKey,
                        deliveryNoteBundleKey:
                            this.data.deliveryNoteBundle._key,
                    }
                );
        }

        return undefined;
    }
}
